import { apiClient } from '@api'

/**
 * Project Service
 *
 * Handles all project-related API calls
 */

const projectService = {
	/**
	 * Get all projects with optional filters
	 * @param {Object} params - Query parameters
	 * @returns {Promise} Projects list
	 */
	async getAll(params = {}) {
		const queryString = new URLSearchParams(params).toString()
		const endpoint = queryString ? `/projects?${queryString}` : '/projects'
		return apiClient.get(endpoint)
	},

	/**
	 * Get project by ID
	 * @param {number} id - Project ID
	 * @returns {Promise} Project data
	 */
	async getById(id) {
		return apiClient.get(`/projects/${id}`)
	},

	/**
	 * Get project by slug
	 * @param {string} slug - Project slug
	 * @param {number} companyId - Company ID
	 * @returns {Promise} Project data
	 */
	async getBySlug(slug, companyId) {
		return apiClient.get(`/projects/slug/${slug}?company_id=${companyId}`)
	},

	/**
	 * Create new project
	 * @param {Object} projectData - Project data
	 * @returns {Promise} Created project
	 */
	async create(projectData) {
		return apiClient.post('/projects', projectData)
	},

	/**
	 * Update project
	 * @param {number} id - Project ID
	 * @param {Object} projectData - Updated data
	 * @returns {Promise} Updated project
	 */
	async update(id, projectData) {
		return apiClient.put(`/projects/${id}`, projectData)
	},

	/**
	 * Delete project (soft delete)
	 * @param {number} id - Project ID
	 * @returns {Promise} Deletion result
	 */
	async delete(id) {
		return apiClient.delete(`/projects/${id}`)
	},

	/**
	 * Restore deleted project
	 * @param {number} id - Project ID
	 * @returns {Promise} Restored project
	 */
	async restore(id) {
		return apiClient.post(`/projects/${id}/restore`)
	},

	/**
	 * Duplicate project
	 * @param {number} id - Project ID to duplicate
	 * @returns {Promise} New project
	 */
	async duplicate(id) {
		return apiClient.post(`/projects/${id}/duplicate`)
	},

	/**
	 * Update project status
	 * @param {number} id - Project ID
	 * @param {string} status - New status
	 * @returns {Promise} Updated project
	 */
	async updateStatus(id, status) {
		return apiClient.put(`/projects/${id}/status`, { status })
	},

	/**
	 * Update project progress
	 * @param {number} id - Project ID
	 * @param {number} progress - Progress percentage (0-100)
	 * @returns {Promise} Updated project
	 */
	async updateProgress(id, progress) {
		return apiClient.put(`/projects/${id}/progress`, { progress })
	},

	/**
	 * Get project certificates
	 * @param {number} id - Project ID
	 * @returns {Promise} Certificates list
	 */
	async getCertificates(id) {
		return apiClient.get(`/projects/${id}/certificates`)
	},

	/**
	 * Add certificate to project
	 * @param {number} projectId - Project ID
	 * @param {number} certificateId - Certificate ID
	 * @returns {Promise} Result
	 */
	async addCertificate(projectId, certificateId) {
		return apiClient.post(`/projects/${projectId}/certificates/${certificateId}`)
	},

	/**
	 * Remove certificate from project
	 * @param {number} projectId - Project ID
	 * @param {number} certificateId - Certificate ID
	 * @returns {Promise} Result
	 */
	async removeCertificate(projectId, certificateId) {
		return apiClient.delete(`/projects/${projectId}/certificates/${certificateId}`)
	},

	/**
	 * Get project statistics
	 * @param {number} id - Project ID
	 * @returns {Promise} Statistics data
	 */
	async getStatistics(id) {
		return apiClient.get(`/projects/${id}/statistics`)
	},

	/**
	 * Get dashboard statistics
	 * @param {number} companyId - Company ID
	 * @returns {Promise} Dashboard data
	 */
	async getDashboard(companyId) {
		return apiClient.get(`/projects/dashboard?company_id=${companyId}`)
	},

	/**
	 * Generate AI checklist for project
	 * @param {number} projectId - Project ID
	 * @returns {Promise} Generated checklist
	 */
	async generateChecklist(projectId) {
		return apiClient.post(`/projects/${projectId}/generate-checklist`)
	},

	/**
	 * Get associated roles for a project
	 * @param {number} projectId - Project ID
	 * @returns {Promise} Associated roles
	 */
	async getAssociatedRoles(projectId) {
		return apiClient.get(`/projects/${projectId}/associated-roles`)
	},

	/**
	 * Sync associated roles for a project (replace all existing)
	 * @param {number} projectId - Project ID
	 * @param {Array} roleIds - Array of role IDs
	 * @returns {Promise} Sync result
	 */
	async syncRoles(projectId, roleIds) {
		return apiClient.put(`/projects/${projectId}/sync-roles`, { role_ids: roleIds })
	},
}

export default projectService
